// src/screens/KioskAppointmentSearchScreen.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import useKioskInactivity from '../hooks/useKioskInactivity';
import { kioskAPI } from '../services/api';

const KioskAppointmentSearchScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // 2-minute global inactivity timer
  useKioskInactivity();

  const [email, setEmail] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'kiosk') {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter a customer email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSearched(true);

      const { data } = await kioskAPI.searchAppointments(email.trim());

      setAppointments(data);
    } catch (err) {
      console.error('Search error:', err);
      const msg = err.response?.data?.message || 'Failed to search appointments';
      setError(msg);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentClick = (appointmentId) => {
    navigate(`/kiosk/appointments/${appointmentId}`);
  };

  const handleReset = () => {
    setEmail('');
    setAppointments([]);
    setError('');
    setSearched(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-app-success text-black';
      case 'Pending':
        return 'bg-yellow-400/90 text-black';
      case 'Vehicle Ready':
        return 'bg-cyan-500/90 text-black';
      case 'Checked In':
        return 'bg-purple-500/90 text-white';
      case 'Completed':
        return 'bg-blue-500/90 text-white';
      case 'Canceled':
        return 'bg-red-500 text-white';
      case 'No Show':
        return 'bg-orange-500/90 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="flex flex-col h-full">
      <AppHeader title="Search Appointments" />

      <div className="flex-1 overflow-y-auto p-4">
        <form onSubmit={handleSearch} className="space-y-3 mb-4">
          <div>
            <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Customer Email</label>
            <input
              type="email"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-sm outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-2xl bg-app-accent text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>

          {searched && (
            <button
              type="button"
              onClick={handleReset}
              className="w-full py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90"
            >
              New Search
            </button>
          )}
        </form>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        {searched && !loading && !error && appointments.length === 0 && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-6 border border-app-border-light dark:border-app-border text-center space-y-4">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-app-text-light dark:text-app-text text-sm font-semibold">No appointments found</p>
            <p className="text-app-text-secondary-light dark:text-app-text-secondary text-xs">
              No appointments found for <span className="text-app-text-light dark:text-app-text">{email}</span>
            </p>

            <div className="pt-2 space-y-2">
              <button
                onClick={() => navigate('/kiosk/book')}
                className="w-full py-3 rounded-xl bg-app-accent text-black text-sm font-semibold hover:opacity-90"
              >
                Create New Appointment
              </button>
              <button
                onClick={handleReset}
                className="w-full py-3 rounded-xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {appointments.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Found {appointments.length} appointment(s)</p>

            {appointments.map((appointment) => {
              const vehicle = appointment.vehicles?.[0];
              const dateStr = appointment.date
                ? new Date(appointment.date).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '';

              return (
                <div
                  key={appointment._id}
                  onClick={() => handleAppointmentClick(appointment._id)}
                  className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border cursor-pointer hover:border-app-accent transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      {vehicle && (
                        <div className="text-sm font-semibold truncate">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </div>
                      )}
                      <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                        {dateStr} • {appointment.time}
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${getStatusColor(
                        appointment.status
                      )}`}
                    >
                      {appointment.status}
                    </span>
                  </div>

                  {appointment.status === 'Vehicle Ready' && (
                    <div className="mt-2 pt-2 border-t border-app-border-light dark:border-app-border">
                      <p className="text-xs text-cyan-400 font-semibold">
                        Ready for check-in - Tap to view QR code
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default KioskAppointmentSearchScreen;
